import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import useFeatures from "@hooks/use-features";
import { usePrice } from "@hooks/use-prices";
import useCustomizableCloset, {
  useReloadCustomizableCloset,
  useAnticipatedAddOnCount,
  useAnticipatedAddOnError,
  useDecreaseAnticipatedAddOnCount,
  useIncreaseAnticipatedAddOnCount,
  useChangeAnticipatedAddOnError,
} from "@hooks/use-customizable-closet";
import { getAddOnsSummary } from "@utils/call-api/closets-call";
import Image from "@components/core/Image";
import IconSvg from "@components/core/IconSvg";
import CounterButton from "./CounterButton";
import classNames from "classnames";

function ClosetRTCFillingAddOn() {
  const t = useTranslations("common");

  const { max_add_on_items: maxAddOnItems } = useFeatures();
  const price = usePrice();
  const currentCloset = useCustomizableCloset();
  const reloadCurrentCloset = useReloadCustomizableCloset();
  const anticipatedAddOnCount = useAnticipatedAddOnCount();
  const anticipatedAddOnError = useAnticipatedAddOnError();

  const decreaseAnticipatedAddOnCount = useDecreaseAnticipatedAddOnCount();
  const increaseAnticipatedAddOnCount = useIncreaseAnticipatedAddOnCount();
  const changeAnticipatedAddOnError = useChangeAnticipatedAddOnError();

  const [anticipatedAddOnsSummary, setAnticipatedAddOnsSummary] = useState(
    currentCloset.add_ons_summary || {},
  );

  const addOnsInClosetCount = currentCloset.add_ons_summary?.add_ons_count;
  const decreaseDisabled = anticipatedAddOnCount === addOnsInClosetCount;
  const increaseDisabled = anticipatedAddOnCount >= maxAddOnItems;

  async function updateAddOnsSummary(newAddOnCount) {
    try {
      const res = await getAddOnsSummary(currentCloset.id, { add_ons_count: newAddOnCount });

      setAnticipatedAddOnsSummary(res);
    } catch (err) {
      reloadCurrentCloset();
    }
  }

  function removeAddOnToCount() {
    changeAnticipatedAddOnError("");
    if (anticipatedAddOnCount === 0 && addOnsInClosetCount === 0) return;

    if (anticipatedAddOnCount - 1 < addOnsInClosetCount) {
      changeAnticipatedAddOnError(
        t("drawer.modal_rtc.change.add_on_counter.remove_errors.remove_item"),
      );
      return;
    }

    updateAddOnsSummary(anticipatedAddOnCount - 1);
    decreaseAnticipatedAddOnCount();
  }

  function addAddOnToCount() {
    changeAnticipatedAddOnError("");

    if (anticipatedAddOnCount + 1 > maxAddOnItems) {
      changeAnticipatedAddOnError(
        t("drawer.modal_rtc.change.add_on_counter.add_error", { maxAddOnItems }),
      );
      return;
    }

    updateAddOnsSummary(anticipatedAddOnCount + 1);
    increaseAnticipatedAddOnCount();
  }

  function addOnsPriceText() {
    if (anticipatedAddOnCount === 0) return price(0);

    const { add_on_credits_to_spend: addOnCreditsToSpend, rest_to_pay: restToPay } =
      anticipatedAddOnsSummary;

    const restToPayText = price(restToPay);
    const addOnCreditsToSpendText = t("drawer.modal_rtc.change.add_on_counter.free", {
      count: addOnCreditsToSpend,
    });
    const addOnCreditsToSpendPart = addOnCreditsToSpend
      ? `${restToPay ? `${addOnCreditsToSpend} ` : ""}${addOnCreditsToSpendText}`
      : "";

    return `${addOnCreditsToSpendPart}${addOnCreditsToSpend && restToPay ? " + " : ""}${restToPay ? restToPayText : ""}`;
  }

  useEffect(() => {
    updateAddOnsSummary(anticipatedAddOnCount);
  }, [anticipatedAddOnCount]);

  return (
    <div className="w-100 p-3 bg-white rounded-sm">
      <div className="w-100 justify-content-between d-flex">
        <div className="gap-3 d-flex">
          <div style={{ minWidth: "36px", maxWidth: "36px", minHeight: "45px", maxHeight: "45px" }}>
            <Image
              src="bonusRetro"
              alt={t("drawer.modal_rtc.change.add_on_counter.bonus_alt")}
              className="w-100"
            />
          </div>

          <div className="align-content-between">
            <p className="mb-0 font-size-sm">
              {t("drawer.modal_rtc.change.add_on_counter.bonus_items")}
            </p>

            <p className="mb-0 font-size-sm">{addOnsPriceText()}</p>
          </div>
        </div>

        <div className="add-on-counter-buttons-container">
          <CounterButton onClick={removeAddOnToCount}>
            <div className={classNames("counter-symbol", decreaseDisabled && "disabled")}>
              <IconSvg
                name="symbol-decrease"
                className="w-100"
              />
            </div>
          </CounterButton>
          <div className="add-on-counter-nbr">
            <span>{anticipatedAddOnCount}</span>
          </div>
          <CounterButton onClick={addAddOnToCount}>
            <div className={classNames("counter-symbol", increaseDisabled && "disabled")}>
              <IconSvg
                name="symbol-increase"
                className="w-100"
              />
            </div>
          </CounterButton>
        </div>
      </div>

      {anticipatedAddOnError && (
        <div className="d-flex justify-content-center align-items-center mt-2">
          <div className="add-on-info">
            <IconSvg
              name="circle-info"
              className="w-100"
            />
          </div>
          <span className="text-primary font-size-sm">{anticipatedAddOnError}</span>
        </div>
      )}
    </div>
  );
}

export default ClosetRTCFillingAddOn;
